
import "./slide-button.js";

import type { EditorHost } from '@blocksuite/block-std';
import type { BlockModel } from '@blocksuite/store';
import { getLastParagraphBlock } from "@pique/core/components/blocksuite/block-suite-mode-switch/augmentation-engine/utils.js";
import type { PropertyValues } from "lit";
import { css, html, LitElement } from "lit";
import {
  customElement,
  property,
  queryAssignedElements,
} from "lit/decorators.js";

import type {
  AnimationTuple
} from "./constants.js";
import {
  BOOTSTRAP_CHEVRON_LEFT,
  BOOTSTRAP_CHEVRON_RIGHT,
  SLIDE_LEFT_IN,
  SLIDE_LEFT_OUT,
  SLIDE_RIGHT_IN,
  SLIDE_RIGHT_OUT,
} from "./constants.js";

@customElement("simple-carousel")
export class SimpleCarousel extends LitElement {
  static override styles = css`
    ::slotted(.slide-hidden) {
      display: none;
    }

    /** So the elements all overlap */
    ::slotted(*) {
      position: absolute;
    }

    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 500px;
      height: 100%;
      gap: 0;
    }

    #carousel-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      flex: 1;
      gap: 0;
      padding: 0;
    }

    #container {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      overflow: hidden;
      position: relative;
      height: 100%;
    }

    #breadcrumbs {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      padding: 16px 0;
      margin-top: 8px;
    }

    .breadcrumb-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: var(--affine-border-color);
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .breadcrumb-dot.active {
      background-color: var(--affine-primary-color);
      transform: scale(1.2);
    }

    .breadcrumb-dot:hover {
      background-color: var(--affine-primary-color);
      opacity: 0.7;
    }

    #save-button {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 48px;
      height: 48px;
      cursor: pointer;
      border-radius: 24px;
      z-index: 100;
      opacity: 0;
      transition: all 0.3s ease;

      display: flex;
      align-items: center;
      justify-content: center;

      background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85));
      backdrop-filter: blur(12px);
      box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.8),
        inset 0 -1px 0 rgba(0, 0, 0, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    #save-button:active {
      transform: translate(-50%, -50%) scale(0.95);
      box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.15),
        inset 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    #container:hover #save-button {
      opacity: 1;
    }

    #save-button:hover {
      opacity: 1 !important;
      transform: translate(-50%, -50%) scale(1.05);
    }

    #save-button svg {
      width: 20px;
      height: 20px;
      opacity: 0.8;
      color: var(--affine-primary-color);
    }

    slide-button {
      z-index: 10;
    }
  `;

  // Assume this is always a valid slide index.
  @property({ type: Number }) accessor slideIndex = 0;
  @queryAssignedElements() private accessor slideElements!: HTMLElement[];
  @property({ attribute: false }) accessor host!: EditorHost;

  /**
   * Return slide index in the range of [0, slideElement.length)
   */
  get wrappedIndex(): number {
    return wrapIndex(this.slideIndex, this.slideElements.length);
  }

  override render() {
    return html`
      <div id="carousel-container">
        <slide-button
          part="buttons left-buton"
          exportparts="internal-btn : buttons"
          @click=${this.navigateToPrevSlide}
        >
          ${BOOTSTRAP_CHEVRON_LEFT}
        </slide-button>

        <div part="container" id="container">
          <slot @slotchange=${this.handleSlotChange}></slot>
          <div id="save-button" @click=${this.handleSaveSlide}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 2v8l3-3 3 3V2"/>
              <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"/>
            </svg>
          </div>
        </div>

        <slide-button
          part="buttons right-buton"
          exportparts="internal-btn : buttons"
          @click=${this.navigateToNextSlide}
        >
          ${BOOTSTRAP_CHEVRON_RIGHT}
        </slide-button>
      </div>

      <div id="breadcrumbs">
        ${this.slideElements.map((_, index) => html`
          <div
            class="breadcrumb-dot ${index === this.wrappedIndex ? 'active' : ''}"
            @click=${() => this.navigateToSlide(index)}
          ></div>
        `)}
      </div>`;
  }

  override firstUpdated() {
    this.initializeSlides();
  }

  private handleSlotChange() {
    this.initializeSlides();
  }

  override updated(changedProperties: PropertyValues<this>): void {
    // Not covered in the video, but if you want to drive animations from the
    // 'slideindex' attribute and property, we can calculate the animation in
    // the 'updated' lifecycle callback.
    if (changedProperties.has("slideIndex")) {
      const oldSlideIndex = changedProperties.get("slideIndex");
      if (oldSlideIndex === undefined) {
        return;
      }
      const isAdvancing = this.slideIndex > oldSlideIndex;

      if (isAdvancing) {
        // Animate forwards
        this.navigateWithAnimation(1, SLIDE_LEFT_OUT, SLIDE_RIGHT_IN).catch(() => {
          // Animation was cancelled
        });
      } else {
        // Animate backwards
        this.navigateWithAnimation(-1, SLIDE_RIGHT_OUT, SLIDE_LEFT_IN).catch(() => {
          // Animation was cancelled
        });
      }
    }
  }

  navigateToNextSlide() {
    // Animation driven by the `updated` lifecycle.
    this.slideIndex += 1;
  }

  navigateToPrevSlide() {
    // Animation driven by the `updated` lifecycle.
    this.slideIndex -= 1;
  }

  private handleSaveSlide() {
    if (!this.host) {
      console.error('Host is not available');
      return;
    }

    const doc = this.host.doc
    const lastParagraphBlock = getLastParagraphBlock(this.host);
    let noteBlock = lastParagraphBlock?.parent;

    if (!noteBlock) {
      console.warn('No note block found, creating a new one.');
      const newNoteId = doc.addBlock('affine:note', {}, doc.root?.id);
      noteBlock = doc.getBlockById(newNoteId) as BlockModel;
    }

    if (noteBlock) {
      const currentSlideElement = this.slideElements[this.wrappedIndex];
      if (!currentSlideElement) return;
      const iframe = currentSlideElement.querySelector('iframe');
      const slideHtml = iframe?.srcdoc;

      if (slideHtml) {
        doc.addBlock(
          'affine:embed-pique-html' as any,
          {
            html: slideHtml,
          },
          noteBlock.id
        );
      }
    }
  }

  private async navigateWithAnimation(
    nextSlideOffset: number,
    leavingAnimation: AnimationTuple,
    enteringAnimation: AnimationTuple
  ) {
    // The slide we're moving from
    const leavingElIndex = wrapIndex(
      this.slideIndex - nextSlideOffset,
      this.slideElements.length
    );
    const elLeaving = this.slideElements[leavingElIndex];
    if (!elLeaving) return;

    // The slide we're moving to
    const elEntering = this.slideElements[this.wrappedIndex];
    if (!elEntering) return;

    // Set the state for the animation: both slides visible, with the entering one on top.
    // First, hide all slides.
    for (const slide of this.slideElements) {
      hideSlide(slide);
    }
    // Then show the two we need for the transition.
    showSlide(elLeaving);
    showSlide(elEntering);

    elLeaving.style.zIndex = '1';
    elEntering.style.zIndex = '2';

    // Animate both slides.
    const leavingAnim = elLeaving.animate(
      leavingAnimation[0],
      leavingAnimation[1]
    );
    const enteringAnim = elEntering.animate(
      enteringAnimation[0],
      enteringAnimation[1]
    );

    // After the animations are finished, we will hide the slide that left.
    await Promise.allSettled([leavingAnim.finished, enteringAnim.finished]);

    // Reset to a clean state where only the active slide is visible.
    this.initializeSlides();
  }

  private initializeSlides() {
    for (let i = 0; i < this.slideElements.length; i++) {
      const slide = this.slideElements[i];
      if (!slide) continue;
      slide.getAnimations().forEach(anim => anim.cancel());

      if (i === this.wrappedIndex) {
        showSlide(slide);
        slide.style.zIndex = '1';
      } else {
        hideSlide(slide);
        slide.style.zIndex = '0';
      }
    }
  }

  navigateToSlide(targetIndex: number) {
    this.slideIndex = targetIndex;
  }
}

function hideSlide(el: HTMLElement) {
  el.classList.add("slide-hidden");
}

function showSlide(el: HTMLElement) {
  el.classList.remove("slide-hidden");
}

function wrapIndex(idx: number, max: number): number {
  return ((idx % max) + max) % max;
}

declare global {
  interface HTMLElementTagNameMap {
    "simple-carousel": SimpleCarousel;
  }
}
